import { useAppDispatch, useAppSelector, useAppStore } from '@repo/store/maps/hooks';
import { selectIsAuthenticated } from '@repo/store/maps/slices/auth';
import { selectHighlightedPinIds, selectSidebarView } from '@repo/store/maps/slices/clientContext';
import { addMapLocationFeature, clearSelectedHeatmapEvent, selectIsAddingCustomLocation, selectIsAddingMapLocation, selectIsBulkMovingLocations, selectIsBulkMovingSelection, selectIsBulkSelectingPins, selectIsEditingNewlyCreatedPin, selectIsInCreatorMode, selectIsMovingLocation, selectLastSelectedCategoryId, selectLastSelectedSubcategoryId, selectMapData, setPreviewMode, setSelectedLabelId, setSelectedRegionId } from '@repo/store/maps/slices/mapData';
import { DEFAULT_PIN_COLOR } from '@repo/store/maps/utils/mapData';
import type { SelectLocationSource } from '@repo/types/analytic-events/maps-analytic-events';
import type { MapDataLocationFeature } from '@repo/types/maps/map-types';
import type * as MapLibre from 'maplibre-gl';
import { useCallback, useEffect, useRef } from 'react';
import { useMapContext } from '../map-context';
import { isMapUsable } from '../map-lifecycle';
import { hasVisiblePinInteractionLayer } from './constants';
import { useCreatorToolOperations } from './useCreatorToolOperations';
import { useCustomPinOperations } from './useCustomPinOperations';
import { MARKER_LAYER_PREFIX } from './useHeatmapLayer/constants';
import { useMapPinOperations } from './useMapPinOperations';
import { useVisiblePinHitTester } from './useVisiblePinHitTester';
const getQueryableLayerIds = (map: MapLibre.Map, predicate: (layerId: string) => boolean): string[] => {
  if (!isMapUsable(map)) {
    return [];
  }
  return map.getLayersOrder().filter(layerId => predicate(layerId) && map.getLayer(layerId));
};
const queryRenderedFeaturesInLayers = (map: MapLibre.Map, point: MapLibre.Point, predicate: (layerId: string) => boolean): MapLibre.MapGeoJSONFeature[] => {
  const layers = getQueryableLayerIds(map, predicate);
  if (layers.length === 0) return [];
  try {
    return map.queryRenderedFeatures(point, {
      layers
    });
  } catch {
    return [];
  }
};
export function useMapHotkeys() {
  const {
    map
  } = useMapContext();
  const dispatch = useAppDispatch();
  const isInCreatorMode = useAppSelector(selectIsInCreatorMode);
  const isAddingCustomLocation = useAppSelector(selectIsAddingCustomLocation);
  const isAddingMapLocation = useAppSelector(selectIsAddingMapLocation);
  const isMovingLocation = useAppSelector(selectIsMovingLocation);
  const isBulkMovingLocations = useAppSelector(selectIsBulkMovingLocations);
  const isBulkSelectingPins = useAppSelector(selectIsBulkSelectingPins);
  const isBulkMovingSelection = useAppSelector(selectIsBulkMovingSelection);
  const isEditingNewlyCreatedPin = useAppSelector(selectIsEditingNewlyCreatedPin);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const lastSelectedCategoryId = useAppSelector(selectLastSelectedCategoryId);
  const lastSelectedSubcategoryId = useAppSelector(selectLastSelectedSubcategoryId);
  const mapData = useAppSelector(selectMapData);
  const store = useAppStore();
  const {
    selectMarkerInMap,
    clearSelectedMarkerInMap
  } = useMapPinOperations();
  const {
    startAddingCustomPin
  } = useCustomPinOperations();
  const {
    createMapLocationAtCoordinates
  } = useCreatorToolOperations();
  const findVisiblePinAtPoint = useVisiblePinHitTester();
  const getMapClickSource = useCallback((markerId: string): SelectLocationSource => {
    const state = store.getState();
    const sidebarView = selectSidebarView(state);
    if (sidebarView === 'checklist' || sidebarView === 'customChecklist' || sidebarView === 'sharedChecklist') {
      return 'map_checklist';
    }
    if (sidebarView === 'heroAction' && selectHighlightedPinIds(state).includes(markerId)) {
      return 'map_hero_action';
    }
    if (selectHighlightedPinIds(state).includes(markerId)) {
      return 'map_explore';
    }
    return 'map_default';
  }, [store]);
  const isInCreatorModeRef = useRef(isInCreatorMode);
  const isAddingCustomLocationRef = useRef(isAddingCustomLocation);
  const isAddingMapLocationRef = useRef(isAddingMapLocation);
  const isMovingLocationRef = useRef(isMovingLocation);
  const isBulkMovingLocationsRef = useRef(isBulkMovingLocations);
  const isBulkSelectingPinsRef = useRef(isBulkSelectingPins);
  const isBulkMovingSelectionRef = useRef(isBulkMovingSelection);
  const lastSelectedCategoryIdRef = useRef(lastSelectedCategoryId);
  const lastSelectedSubcategoryIdRef = useRef(lastSelectedSubcategoryId);
  const isEditingNewlyCreatedPinRef = useRef(isEditingNewlyCreatedPin);
  const mapRef = useRef<MapLibre.Map | null>(null);
  const mapDataRef = useRef(mapData);
  const isCreatingPinRef = useRef(false);
  useEffect(() => {
    isInCreatorModeRef.current = isInCreatorMode;
    isAddingCustomLocationRef.current = isAddingCustomLocation;
    isAddingMapLocationRef.current = isAddingMapLocation;
    isMovingLocationRef.current = isMovingLocation;
    isBulkMovingLocationsRef.current = isBulkMovingLocations;
    isBulkSelectingPinsRef.current = isBulkSelectingPins;
    isBulkMovingSelectionRef.current = isBulkMovingSelection;
    isEditingNewlyCreatedPinRef.current = isEditingNewlyCreatedPin;
    lastSelectedCategoryIdRef.current = lastSelectedCategoryId;
    lastSelectedSubcategoryIdRef.current = lastSelectedSubcategoryId;
    mapDataRef.current = mapData;
  }, [isInCreatorMode, isAddingCustomLocation, isAddingMapLocation, isMovingLocation, isBulkMovingLocations, isBulkSelectingPins, isBulkMovingSelection, isEditingNewlyCreatedPin, lastSelectedCategoryId, lastSelectedSubcategoryId, mapData]);
  const createPinAtLocationAndEdit = useCallback(async (lng: number, lat: number) => {
    if (!mapRef.current) return;
    if (isCreatingPinRef.current) return;
    if (!isAuthenticated) {
      return;
    }
    isCreatingPinRef.current = true;
    try {
      if (isInCreatorModeRef.current) {
        const newLocation = await createMapLocationAtCoordinates(lng, lat, undefined, {
          lastSelectedCategoryId: lastSelectedCategoryIdRef.current,
          lastSelectedSubcategoryId: lastSelectedSubcategoryIdRef.current
        });
        if (newLocation) {
          dispatch(addMapLocationFeature(newLocation));
          selectMarkerInMap(newLocation.properties.externalId, {
            feature: newLocation,
            isNewlyCreated: true
          });
        }
      } else {
        // In user mode, start adding a custom pin with pre-filled coordinates
        await startAddingCustomPin({
          title: 'Custom Pin',
          description: '',
          color: DEFAULT_PIN_COLOR,
          coordinates: [lng, lat]
        }, mapRef.current);
      }
    } finally {
      isCreatingPinRef.current = false;
    }
  }, [createMapLocationAtCoordinates, selectMarkerInMap, startAddingCustomPin, isAuthenticated, dispatch]);
  const handlePinClick = useCallback((pin: MapDataLocationFeature, e: MapLibre.MapMouseEvent & {
    features?: MapLibre.MapGeoJSONFeature[];
  }, modifiers: {
    ctrlKey: boolean;
    shiftKey: boolean;
  }) => {
    const externalId = pin.properties.externalId;
    if (!externalId) return;

    // Handle different click combinations
    if (modifiers.shiftKey) {
      // Shift + click: Create pin immediately at location and open for editing
      void createPinAtLocationAndEdit(e.lngLat.lng, e.lngLat.lat);
    } else if (modifiers.ctrlKey && isInCreatorModeRef.current) {
      // Ctrl + click in creator mode: Open pin in edit mode
      dispatch(setPreviewMode(false)); // Disable preview mode
      dispatch(setSelectedLabelId(null)); // Clear any selected label
      dispatch(setSelectedRegionId(null)); // Clear any selected region
      selectMarkerInMap(externalId, {
        selectSource: getMapClickSource(externalId)
      });
      // The edit mode will be handled by the existing location info system
    } else if (isInCreatorModeRef.current) {
      // Left click in creator mode: Open pin in preview mode
      dispatch(setPreviewMode(true)); // Enable preview mode
      selectMarkerInMap(externalId, {
        selectSource: getMapClickSource(externalId)
      });
      // The preview mode will be handled by the existing location info system
    } else {
      // Default behavior: Select marker
      dispatch(setPreviewMode(false)); // Disable preview mode
      selectMarkerInMap(externalId, {
        selectSource: getMapClickSource(externalId)
      });
    }
  }, [selectMarkerInMap, createPinAtLocationAndEdit, dispatch, getMapClickSource]);
  const handleMapClick = useCallback((e_0: MapLibre.MapMouseEvent & {
    features?: MapLibre.MapGeoJSONFeature[];
  }) => {
    if (isAddingCustomLocationRef.current || isAddingMapLocationRef.current || isMovingLocationRef.current || isBulkMovingLocationsRef.current) {
      return;
    }

    // Don't handle map clicks if there are features (markers) at this location
    // This prevents interference with marker click handling
    if (e_0.features && e_0.features.length > 0) {
      return;
    }

    // Check for shift+click on empty map areas
    if (e_0.originalEvent.shiftKey) {
      void createPinAtLocationAndEdit(e_0.lngLat.lng, e_0.lngLat.lat);
      return;
    }
    if (isEditingNewlyCreatedPinRef.current) {
      // Trigger wiggle animation for newly created pins
      const locationInfoElement = document.getElementById('location-info');
      if (locationInfoElement) {
        locationInfoElement.style.animation = 'wiggle 0.3s ease-in-out';
        setTimeout(() => {
          locationInfoElement.style.animation = '';
        }, 500);
      }
    } else {
      clearSelectedMarkerInMap();
      dispatch(clearSelectedHeatmapEvent());
    }
  }, [clearSelectedMarkerInMap, createPinAtLocationAndEdit, dispatch]);
  const handleLabelClick = useCallback((e_1: MapLibre.MapMouseEvent & {
    features?: MapLibre.MapGeoJSONFeature[];
  }, modifiers_0: {
    ctrlKey: boolean;
    shiftKey: boolean;
    mKey: boolean;
  }) => {
    if (!e_1?.features?.length || !isInCreatorModeRef.current) {
      return;
    }
    const feature = e_1.features[0];
    if (!feature?.properties?.externalId) {
      return;
    }
    const label = mapDataRef.current?.labels?.features.find(l => l.properties.externalId === feature.properties.externalId);
    if (!label) return;

    // Ctrl + click: Select label for editing in sidebar
    if (modifiers_0.ctrlKey) {
      dispatch(setPreviewMode(false)); // Disable preview mode
      dispatch(setSelectedRegionId(null)); // Clear any selected region
      clearSelectedMarkerInMap(); // Clear any selected pin
      dispatch(setSelectedLabelId(label.properties.externalId));
    }
  }, [dispatch, clearSelectedMarkerInMap]);
  const handleRegionClick = useCallback((e_2: MapLibre.MapMouseEvent & {
    features?: MapLibre.MapGeoJSONFeature[];
  }, modifiers_1: {
    ctrlKey: boolean;
    shiftKey: boolean;
    mKey: boolean;
  }) => {
    if (!e_2?.features?.length || !isInCreatorModeRef.current) {
      return;
    }
    const feature_0 = e_2.features[0];
    if (!feature_0?.properties?.externalId) {
      return;
    }
    const region = mapDataRef.current?.regions?.features.find(r => r.properties.externalId === feature_0.properties.externalId);
    if (!region) return;

    // Ctrl + click: Select region for editing in sidebar
    if (modifiers_1.ctrlKey) {
      dispatch(setPreviewMode(false)); // Disable preview mode
      dispatch(setSelectedLabelId(null)); // Clear any selected label
      clearSelectedMarkerInMap(); // Clear any selected pin
      dispatch(setSelectedRegionId(region.properties.externalId));
    }
  }, [dispatch, clearSelectedMarkerInMap]);
  const initializeHotkeys = useCallback((mapInstance?: MapLibre.Map) => {
    const targetMap = mapInstance || map;
    if (!targetMap) {
      return;
    }
    mapRef.current = targetMap;

    // Track M key state
    let mKeyPressed = false;
    const handleKeyDown = (e_3: KeyboardEvent) => {
      if (e_3.key === 'm' || e_3.key === 'M') {
        mKeyPressed = true;
      }
    };
    const handleKeyUp = (e_4: KeyboardEvent) => {
      if (e_4.key === 'm' || e_4.key === 'M') {
        mKeyPressed = false;
      }
    };

    // Handle all clicks (map, markers, labels, and regions)
    const handleMapClickEvent = (e_5: MapLibre.MapMouseEvent & {
      features?: MapLibre.MapGeoJSONFeature[];
    }) => {
      if (!isMapUsable(targetMap) || isBulkMovingLocationsRef.current || isBulkSelectingPinsRef.current || isBulkMovingSelectionRef.current) {
        return;
      }
      const drawFeatures = queryRenderedFeaturesInLayers(targetMap, e_5.point, layerId => layerId.startsWith('gl-draw-'));
      if (drawFeatures.length > 0) {
        return; // Let MapboxDraw handle this click
      }
      const modifiers_2 = {
        ctrlKey: e_5.originalEvent.ctrlKey || e_5.originalEvent.metaKey,
        shiftKey: e_5.originalEvent.shiftKey,
        mKey: mKeyPressed
      };
      const pin_0 = hasVisiblePinInteractionLayer(targetMap) ? findVisiblePinAtPoint(targetMap, e_5.point) : null;
      if (pin_0) {
        handlePinClick(pin_0, e_5, {
          ctrlKey: modifiers_2.ctrlKey,
          shiftKey: modifiers_2.shiftKey
        });
        return;
      }
      const heatmapMarkerFeatures = queryRenderedFeaturesInLayers(targetMap, e_5.point, layerId_0 => layerId_0.startsWith(MARKER_LAYER_PREFIX));
      if (heatmapMarkerFeatures.length > 0) {
        // Layer-specific handler in useHeatmapLayer owns selection.
        return;
      }

      // Only handle label/region clicks if Ctrl is pressed and no pins were found
      if (modifiers_2.ctrlKey && isInCreatorModeRef.current) {
        const labelFeatures = queryRenderedFeaturesInLayers(targetMap, e_5.point, layerId_1 => layerId_1.startsWith('label-text'));
        if (labelFeatures && labelFeatures.length > 0) {
          const eventWithFeatures = {
            ...e_5,
            features: labelFeatures
          } as MapLibre.MapMouseEvent & {
            features?: MapLibre.MapGeoJSONFeature[];
          };
          handleLabelClick(eventWithFeatures, modifiers_2);
          return;
        }

        // Check for regions
        const regionFeatures = queryRenderedFeaturesInLayers(targetMap, e_5.point, layerId_2 => layerId_2.startsWith('region-fills') || layerId_2.startsWith('region-outlines'));
        if (regionFeatures && regionFeatures.length > 0) {
          const eventWithFeatures_0 = {
            ...e_5,
            features: regionFeatures
          } as MapLibre.MapMouseEvent & {
            features?: MapLibre.MapGeoJSONFeature[];
          };
          handleRegionClick(eventWithFeatures_0, modifiers_2);
          return;
        }
      }

      // No features, handle as map click
      handleMapClick(e_5);
    };
    targetMap.on('click', handleMapClickEvent);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    // Return cleanup function
    return () => {
      targetMap.off('click', handleMapClickEvent);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [map, findVisiblePinAtPoint, handlePinClick, handleMapClick, handleLabelClick, handleRegionClick]);
  return {
    initializeHotkeys
  };
}